import { ReactComponent as SendIcon } from "icons/aisummary/send.svg";
import { ReactComponent as StopIcon } from "icons/aisummary/stop.svg";
import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "components/mui-core";

const useStyles = makeStyles()({
  inputContainer: {
    position: "sticky",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    backgroundColor: "#fff",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "28px",
    padding: "6px 6px 6px 24px",
    transition: "box-shadow 0.2s ease-in-out",
    "&:hover": {
      boxShadow:
        "0 0 0 0.5px rgba(0, 0, 0, 0.10), 0 2px 6px 2px rgba(0, 0, 0, 0.08)",
    },
  },
  textarea: {
    flex: 1,
    border: "none",
    outline: "none",
    resize: "none",
    fontFamily: "inherit",
    fontSize: "14px",
    lineHeight: "20px",
    maxHeight: "150px",
    overflowY: "auto",
    backgroundColor: "transparent",
    "&::placeholder": {
      color: "rgba(0, 0, 0, 0.38)",
    },
  },
  submitButton: {
    padding: "16px 24px",
    borderRadius: "100px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease-in-out",
    flexShrink: 0,
    alignSelf: "flex-end",
    "&:disabled": {
      cursor: "not-allowed",
    },
  },
  submitButtonDefault: {
    backgroundColor: "#B69DF7",
    "&:hover:not(:disabled)": {
      backgroundColor: "#B69DF7",
    },
  },
  submitButtonFocused: {
    backgroundColor: "#6750A4",
  },
  submitButtonLoading: {
    backgroundColor: "#B69DF7",
  },
});

type AIInputProps = {
  onSubmit: (value: string) => void;
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  reAddOnCancel?: boolean;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
};

export const AIInput = ({
  onSubmit,
  onCancel,
  loading,
  disabled,
  placeholder = "Ask anything...",
  reAddOnCancel,
  containerClassName,
  containerStyle,
}: AIInputProps) => {
  const { classes } = useStyles();
  const [value, setValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [lastSubmittedValue, setLastSubmittedValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled && !loading) {
      const trimmedValue = value.trim();
      setLastSubmittedValue(trimmedValue);
      onSubmit(trimmedValue);
      setValue("");
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading && onCancel) {
      onCancel();
      if (reAddOnCancel && lastSubmittedValue) {
        setValue(lastSubmittedValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getButtonClass = () => {
    if (loading) {
      return `${classes.submitButton} ${classes.submitButtonLoading}`;
    }
    if (isFocused) {
      return `${classes.submitButton} ${classes.submitButtonFocused}`;
    }
    return `${classes.submitButton} ${classes.submitButtonDefault}`;
  };

  return (
    <div
      className={`${classes.inputContainer} ${containerClassName || ""}`}
      style={containerStyle}>
      <form
        onSubmit={handleSubmit}
        className={classes.inputWrapper}>
        <textarea
          ref={textareaRef}
          className={classes.textarea}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          rows={1}
        />
        <button
          type={loading ? "button" : "submit"}
          className={getButtonClass()}
          disabled={disabled || (!loading && !value.trim())}
          onClick={loading ? handleCancel : undefined}>
          {loading ? <StopIcon /> : <SendIcon />}
        </button>
      </form>
    </div>
  );
};
